import React from 'react';
import PropTypes from 'prop-types';
import { indexOf, isEqual } from 'lodash';
import Loading from '../Loading';
import Notice from '../../notifications/Notice';
import NotificationsWrapper from '../../notifications/NotificationsWrapper'
import ContactForm from './ContactForm';
import VerifyForm from './VerifyForm';

export default class VerifyContactInfoWrapper extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            account: props.account,
            verifiedAddress: props.verifiedAddress,
            done: props.done,
            showNotice: props.showNotice,
            noticeType: props.noticeType,
            noticeText: props.noticeText,
            updatedAccount: null,
            unableToVerify: props.unableToVerify,
            disableSaveBtn: true,
            originalAddressValid: true
        }

        this.handleChange = this.handleChange.bind(this);
        this.handleOptionChange = this.handleOptionChange.bind(this);
        this.editProfile = this.editProfile.bind(this);
        this.saveProfile = this.saveProfile.bind(this);
    }

    componentDidMount() {
        this.props.verificationRequired();
    }

    componentDidUpdate(prevProps) {
        const {
            account,
            verifiedAddress,
            done,
            showNotice,
            noticeType,
            noticeText,
            unableToVerify
        } = this.props;

        if (account !== prevProps.account && !isEqual(account, this.state.account)) {
            this.setState({ account });
        }

        if (verifiedAddress !== prevProps.verifiedAddress && !isEqual(verifiedAddress, this.state.verifiedAddress)) {
            this.setState({ verifiedAddress });
        }

        if (done !== prevProps.done && !isEqual(done, this.state.done)) {
            this.setState({ done });
        }

        if (showNotice !== prevProps.showNotice && !isEqual(showNotice, this.state.showNotice)) {
            this.setState({ showNotice });
        }

        if (noticeType !== prevProps.noticeType && !isEqual(noticeType, this.state.noticeType)) {
            this.setState({ noticeType });
        }

        if (noticeText !== prevProps.noticeText && !isEqual(noticeText, this.state.noticeText)) {
            this.setState({ noticeText });
        }

        if (unableToVerify !== prevProps.unableToVerify && !isEqual(unableToVerify, this.state.unableToVerify)) {
            this.setState({ unableToVerify });
        }
    }

    editProfile() {
        this.setState({
            verifiedAddress: null,
            updatedAccount: null,
            done: false,
            unableToVerify: false
        });
        this.props.resetUnableToVerify();
    }

    saveProfile() {
        let accountToSave = this.state.updatedAccount;
        if (this.state.account.home_address.country !== 'USA') {
            accountToSave = this.state.account
        }
        this.props.saveProfile(accountToSave);
    }

    handleChange(e) {
        const target = e.target;
        const value = target.value;
        const name = target.name;
        const account = this.state.account;
        if (indexOf(['country', 'address1', 'address2', 'city', 'state', 'zip_code'], name) >= 0) {
            const address = account.home_address;
            address[name] = value;
            if (name === 'country' && value !== 'USA') {
                address.address1 = '';
                address.address2 = '';
                address.city = '';
                address.state = '';
                address.zip_code = '';
            }
            account.home_address = address;
        }
        else {
            account[name] = value;
        }
        this.setState({
            account
        });
    }

    handleOptionChange(e) {
        const updatedAccount = {};
        let disableSaveBtn = false;
        let originalAddressValid = true;
        Object.assign(updatedAccount, this.state.account);
        if (e.target.value === 'suggested') {
            updatedAccount.home_address = this.state.verifiedAddress;
        }
        if (e.target.value === 'original') {
            const originalAddress = updatedAccount.home_address;
            if (!originalAddress.address1 || !originalAddress.city || !originalAddress.state || !originalAddress.zip_code) {
                disableSaveBtn = true;
                originalAddressValid = false;
            }
        }
        if (!e.target.value) {
            disableSaveBtn = true;
        }
        this.setState({
            updatedAccount,
            originalAddressValid,
            disableSaveBtn
        });
    }

    render() {
        if (this.props.noVerificationRequired) {
            return null;
        }
        if (this.state.account === null) {
            return (
                <Loading />
            );
        }
        let content = null;
        if (this.state.account) {
            content = (
                <ContactForm
                    account={this.state.account}
                    handleChange={this.handleChange}
                    saveProfile={this.saveProfile}
                    verifyAddress={this.props.verifyAddress} />
            );
        }

        if (this.state.verifiedAddress || this.state.unableToVerify) {
            content = (
                <VerifyForm
                    verifiedAddress={this.state.verifiedAddress}
                    currentAddress={this.state.account.home_address}
                    handleOptionChange={this.handleOptionChange}
                    saveProfile={this.saveProfile}
                    editProfile={this.editProfile}
                    unableToVerify={this.state.unableToVerify}
                    disableSaveBtn={this.state.disableSaveBtn}
                    originalAddressValid={this.state.originalAddressValid} />
            );
        }
        let doneMessage = "";
        if (this.state.done) {
            doneMessage = (
                <div className="rec-verify-contact-done-message">
                    <NotificationsWrapper noticeType="info">
                        <p>The address has been successfully saved.</p>
                    </NotificationsWrapper>
                </div>
            )
        }
        return (
            <div className="rec-verify-contact">
                <p>
                    Customers who paid in cash will be refunded in the form of a
                    treasury check sent in the mail. Please enter the fields
                    below and verify that you have entered a USPS-recognized address.
                </p>
                <div>
                    <Notice showNotice={this.state.showNotice} noticeType={this.state.noticeType}>
                        {this.state.noticeText}
                    </Notice>
                </div>
                {content}
                {doneMessage}
            </div>
        );
    }
}

VerifyContactInfoWrapper.propTypes = {
    saveProfile: PropTypes.func,
    verifyAddress: PropTypes.func,
    verificationRequired: PropTypes.func,
    account: PropTypes.shape({
        account_id: PropTypes.string,
        first_name: PropTypes.string,
        home_address: PropTypes.shape({
            address1: PropTypes.string,
            address2: PropTypes.string,
            city: PropTypes.string,
            country: PropTypes.string,
            state: PropTypes.string,
            zip_code: PropTypes.string
        }),
        home_phone: PropTypes.string,
        last_name: PropTypes.string
    }),
    verifiedAddress: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip_code: PropTypes.string
    }),
    done: PropTypes.bool,
    showNotice: PropTypes.bool,
    noticeType: PropTypes.string,
    noticeText: PropTypes.string,
    noVerificationRequired: PropTypes.bool,
    unableToVerify: PropTypes.bool,
    resetUnableToVerify: PropTypes.func
};
